uniform float uTime;
uniform vec3 uBackgroundColor;

uniform float uAppearanceDuration;
uniform float uHighPriorityAppearanceRadius;
uniform float uMaxDistance; // Maximum distance from center (for normalization)

// Spherical waves uniforms
uniform vec3 uWaveColor;
uniform float uWaveColorIntensity;

// Vertex color brightness boost (default 1.0 — passthrough). Used by night
// scenes whose preview-GLB vertex colors are baked dark; multiplying lifts
// them into a visible range before wave blend.
uniform float uColorMultiplier;

// Reveal effect uniforms
uniform float uRevealTime;
uniform float uRevealSpeed;
uniform vec3 uRevealOrigin;
uniform float uRevealDistanceScale;
uniform bool uRevealUse3d;

// Varyings
varying vec3 vWorldPosition;
varying vec3 vColor;
varying float vWaveIntensity;
varying float vDistanceFromCenter; // Distance from wave center (from vertex shader)

float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}

float hash2(vec2 p) {
    return fract(sin(dot(p, vec2(269.5, 183.3))) * 43758.5453);
}

void main() {
    // Point shape
    float dist = length(gl_PointCoord - vec2(0.5));
    if (dist > 0.5) discard;
    float alpha = 1.0 - smoothstep(0.0, 0.5, dist);

    // Calculate time scaled by speed
    float t = uRevealTime * uRevealSpeed;

    // Calculate distance from origin (matching splat shader reveal)
    vec3 localPos = vWorldPosition - uRevealOrigin;
    float l = uRevealUse3d ? length(localPos) : length(localPos.xz);

    // Calculate where splats become visible (same logic as splat shader)
    float splatVisibility = 0.0;
    
    // Spread Effect: Gentle radial emergence
    // Formula synchronized with splat shader reveal effect
    float tttt = pow(t, 4.0) * 0.4 + 0.5;
    // Splats become visible when tttt - l * distanceScale > 0
    // uRevealDistanceScale is in world coordinates (same as previewModel)
    splatVisibility = clamp(tttt - l * uRevealDistanceScale, 0.0, 1.0);

    // Preview model should be visible where splats are NOT visible yet
    // So we hide where splats become visible
    float previewVisibility = 1.0 - splatVisibility;

    // Discard fragments that should be hidden
    if (previewVisibility <= 0.0) {
        discard;
    }
    
    // Interpolate color with wave color based on wave intensity
    // Normalize color intensity: map from 0-10 to 0.0-1.0
    float normalizedIntensity = uWaveColorIntensity * 0.1;
    
    // Calculate effective wave intensity: multiply wave intensity by color intensity
    // This controls how pronounced the wave color is
    float effectiveIntensity = vWaveIntensity * normalizedIntensity;
    
    // Boost vertex color before wave blend (saturated to LDR range so large
    // multipliers don't blow out into NaN/HDR on different GPUs).
    vec3 boostedBase = clamp(vColor * uColorMultiplier, 0.0, 1.0);

    // Interpolate between base color and wave color based on effective intensity
    vec3 waveColor = mix(boostedBase, uWaveColor, effectiveIntensity);

    float appearanceTimeProgress = min(uTime / uAppearanceDuration, 1.0);
    float pointHash = hash(localPos.xz);
    
    // Distance-based weight function: gives bonus for close points, penalty for far points
    // Use vDistanceFromCenter (from wave center) instead of l (from reveal origin)
    // uHighPriorityAppearanceRadius is fraction of model size (0-1)
    // Normalize distance relative to priority radius scaled by model size
    float priorityRadius = uHighPriorityAppearanceRadius * uMaxDistance;
    float normalizedDist = vDistanceFromCenter / max(priorityRadius, 0.001);
    
    // Exponential falloff for bonus (stronger near center, decays quickly)
    // Using exp(-x) gives values from 1.0 (at center) to ~0 (far away)
    float bonusWeight = exp(-normalizedDist * 2.0);
    
    // Penalty for far points (starts after normalizedDist > 1.0)
    // Linear penalty that increases with distance
    float penaltyWeight = max(0.0, (normalizedDist - 1.0) * 0.5);
    
    // Combine bonus and penalty: positive values = earlier appearance, negative = later
    float distanceWeight = bonusWeight * 3.5 - penaltyWeight * 1.5;
    
    // Extra hash for randomization
    float additionalHash = hash2(localPos.xz);
    
    // Apply distance-based adjustment to hash
    // Negative adjustment (subtract) = earlier appearance (bonus)
    // Positive adjustment (add) = later appearance (penalty)
    float hashAdjustment = additionalHash * distanceWeight;
    pointHash = clamp(pointHash - hashAdjustment, 0.0, 1.0);
    float transition = 0.02;
    
    // Continuous appearance function: point appears when appearanceTimeProgress exceeds pointHash
    // This ensures: 
    // - At time 0, all points are hidden (appearanceTimeProgress - pointHash < 0 for all points)
    // - Smooth transition with constant speed
    // - No conditional checks needed
    float timeOffset = max(0.0, appearanceTimeProgress - pointHash);
    float appearanceProgress = smoothstep(0.0, transition * 2.0, timeOffset);

    vec3 finalColor = mix(uBackgroundColor, waveColor, appearanceProgress);
    gl_FragColor = vec4(finalColor, alpha * previewVisibility);
}
